import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { SortOrder, VideoInfo } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { deleteHomeVideos, fetchHomeVideos, selectHomeVideos, UIFetchHomeVideosFilters, userAppDataContext }
    from '@moderepo/mode-data-state-user-app';
import { capitalizeAll, capitalizeFirst, ITEMS_PER_PAGE_10, ITEMS_PER_PAGE_LIST, GenericMessageBox, PaginationBar, PAGING_MIN_PAGES, useSearchParams,
    BaseListCompField } from '@moderepo/mode-app-components';
import { DialogResponse, DialogResponseCode, DialogType, modeUIContext, showDialog, showSuccessNotification } from '@moderepo/mode-ui-state';
import produce, { Draft } from 'immer';
import { useRitzTranslation } from '../../utils/customHooks';
import { VideosTable, VideosTableProps } from '../../components/videos/VideosTable';



/**
 * The set of fields to be hidden from the table. By default, we will show all columns. If we need to hide some column,
 * set column to 'true'
 */
export interface VideosListHiddenFields {
    readonly searchKey?: boolean;
    readonly homeId?: boolean;
    readonly thumbnail?: boolean;
    readonly video?: boolean;
}


/**
 * Required/optional props for this component
 */
export interface VideosListingContainerProps {
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId: number;
    readonly videoSmartModuleId: string;
    readonly itemsPerPage?: number | undefined;
    readonly hiddenFields?: VideosListHiddenFields | undefined;
}


/**
 * This container display all the VIDEOS belonged to a home and specific video smart module.
 */
export const VideosListingContainer: React.FC<VideosListingContainerProps> = (
    props: VideosListingContainerProps,
) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);

    const {
        searchParams, onItemsPerPageChange, onPrevPage, onNextPage, onPage, onSearchTextChange, onOtherFieldsChange,
    } = useSearchParams<{
        readonly sortField: string | undefined;
        readonly sortOrder: SortOrder | undefined;
    }>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            sortField: 'searchKey',       // sort the videos by searchKey by default
            sortOrder: SortOrder.DESC,    // Sort the searchKey in DESC order by default
        },
    });


    /**
     * Create and cache the filters we will be using to select/fetch home videos.
     */
    const fetchHomeVideosFilters = useMemo((): UIFetchHomeVideosFilters => {
        return {
            pageNumber     : searchParams.currentPage,
            pageSize       : searchParams.itemsPerPage,
            searchKeyPrefix: searchParams.searchText,
            sortBy         : searchParams.otherFields.sortField,
            sortOrder      : searchParams.otherFields.sortOrder,
        };
    }, [searchParams.currentPage, searchParams.itemsPerPage, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder,
        searchParams.searchText]);


    /**
     * Get the home videos info from the data state
     */
    const allVideos = selectHomeVideos(appDataState, props.homeId, props.videoSmartModuleId, fetchHomeVideosFilters);



    /**
     * This useEffect is used for fetching videos when search params changed
     */
    const fetchVideos = useCallback(() => {
        appDataDispatch(fetchHomeVideos(props.homeId, props.videoSmartModuleId, fetchHomeVideosFilters), true);
    }, [appDataDispatch, props.homeId, props.videoSmartModuleId, fetchHomeVideosFilters]);


    /**
     * This useEffect is used for fetching videos once on component load
     */
    useEffect(() => {
        fetchVideos();
    }, [fetchVideos]);


    /**
     * On column clicked, change the sort order/field
     */
    const onSortByField = useCallback((field: BaseListCompField) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<typeof searchParams.otherFields>) => {
                if (draft.sortField === field.dataItemProp) {
                    // this column is currently being sorted, change the toggle the sort order instead
                    if (draft.sortOrder === SortOrder.ASC) {
                        draft.sortOrder = SortOrder.DESC;
                    } else {
                        draft.sortOrder = SortOrder.ASC;
                    }
                } else if (field.dataItemProp && field.dataItemProp === 'searchKey') {
                    // This column is not the current column being sorted AND it is 'searchKey', change the sortedField this column
                    draft.sortField = field.dataItemProp;
                    draft.sortOrder = SortOrder.DESC;
                } else {
                    // If the user clicked on any other column that is NOT searchKey then clear the 'sortOrder' and 'sortField' and have the back end
                    // return the videos in the order that they are uploaded
                    draft.sortField = undefined;
                    draft.sortOrder = undefined;
                }
            }));
        }
    }, [searchParams, onOtherFieldsChange]);



    /**
     * Define the fields for the Videos table and cache it.
     */
    const videosTableFields = useMemo((): VideosTableProps['fieldsSettings'] => {
        return {
            fields: {
                thumbnail: {
                    label : capitalizeAll(trans('label.thumbnail')),
                    hidden: props.hiddenFields?.thumbnail,
                },
                video: {
                    label       : capitalizeAll(trans('label.video')),
                    hidden      : props.hiddenFields?.video,
                    dataItemProp: 'video',
                    sortable    : searchParams.otherFields.sortField !== undefined,
                },
                homeId: {
                    label : capitalizeAll(trans('label.home_id')),
                    hidden: props.hiddenFields?.homeId,
                },
                searchKey: {
                    label       : capitalizeAll(trans('label.video_search_key')),
                    hidden      : props.hiddenFields?.searchKey,
                    dataItemProp: 'searchKey',
                    sortable    : true,
                },
                remove: {
                    label: '',
                },
            },
            currentSortedField: searchParams.otherFields.sortField,
            currentSortedOrder: searchParams.otherFields.sortOrder,
            onSortByField,
        };
    }, [onSortByField, props.hiddenFields?.homeId, props.hiddenFields?.searchKey, props.hiddenFields?.thumbnail, props.hiddenFields?.video,
        searchParams.otherFields.sortField, searchParams.otherFields.sortOrder, trans]);



    /**
     * convert allVideos to VideosTablePropsDataItem
     */
    const localizedVideosTableData = useMemo((): VideosTableProps['listData'] => {
        if (allVideos) {
            return allVideos.map((videos: VideoInfo) => {
                return {
                    actualValue : videos,
                    canBeRemoved: true,
                    displayValue: {
                        searchKey: videos.searchKey,
                        homeId   : videos.homeId.toString(),
                        thumbnail: videos.thumbnail,
                        video    : videos.video,
                    },
                };
            });
        }

        return [];
    }, [allVideos]);



    /**
     * Create the Pagination component for the VideosTable and cache it
     */
    const videosTablePaginationSettings = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                hasMoreItems    : allVideos !== undefined && allVideos.length >= searchParams.itemsPerPage,
                texts           : {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('label.video', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [searchParams.itemsPerPage, searchParams.currentPage, allVideos, trans, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);



    /**
     * Handler for the Delete button
     */
    const onRemoveVideoClicked = useCallback((videoInfo: VideoInfo) => {
        // Confirm before canceling request
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.generic_confirm_delete.title',
            titleData     : { object_term: capitalizeAll(trans('label.video', { count: 2 })) },
            message       : 'dialog.confirm_delete_video.message',
            messageData   : { search_key: videoInfo.searchKey },
            positiveButton: 'label.yes',
            negativeButton: 'label.no',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {

                    // Dispatch an action to delete the videos
                    appDataDispatch(deleteHomeVideos(props.homeId, props.videoSmartModuleId, videoInfo.searchKey)).then(() => {
                        uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', {
                            term: capitalizeAll(trans('label.video', { count: 2 })),
                        }));

                        // refetch the videos list
                        fetchVideos();
                    }).catch(() => {
                        // ignore error
                    });
                }
            },
        }));
    }, [appDataDispatch, fetchVideos, props.homeId, props.videoSmartModuleId, trans, uiDispatch]);



    return (
        <VideosTable
            icon="videocam"
            title={props.videoSmartModuleId}
            fieldsSettings={videosTableFields}
            listData={localizedVideosTableData}
            texts={{
                searchBox: {
                    placeholder: capitalizeFirst(trans('label.ts_search_placeholder')),
                },
            }}
            enableSearch
            searchTextCaseSensitive={true}
            onSearchTextChanged={onSearchTextChange}
            paginationComp={videosTablePaginationSettings}
            noDataMessageComp={(
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.video', { count: 2 })),
                    })}
                />
            )}
            emptyDataMessageComp={(
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.video', { count: 2 })),
                    })}
                />
            )}
            onRemoveItemClicked={onRemoveVideoClicked}
        />
    );
};
